import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useRef, useState, type ReactElement } from 'react';
import { APP_NAME } from '../../app/brand';
import { isApiError, startSync } from '../../api';
import { Button } from '../details/Button';
import { useFocusRescue } from '../details/use-focus-rescue';
import { adminErrorMessage } from './error-message';
import { formatDateTime } from './format';
import { syncKey } from './keys';
import { syncErrorHelp, syncStateSentence, syncStatRows } from './sync-text';
import { useSyncStatus } from './use-sync';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './SyncSection.module.css';

/**
 * Importação de histórico: traz para o CladvApp as conversas do WhatsApp e o histórico recente delas.
 * Mostra o estado, as contagens e o erro; enquanto roda, acompanha o andamento sozinha (ver
 * `useSyncStatus`). Só é montada quando a conexão diz que a instalação importa (`canSync`); se o
 * servidor mesmo assim responder 404, a seção some, sem erro.
 *
 * O texto do erro do servidor NÃO é exibido: ele pode nomear o serviço por trás da conexão, e a
 * interface não o nomeia (ver `syncErrorHelp`).
 */
export function SyncSection(): ReactElement | null {
  const queryClient = useQueryClient();
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const sync = useSyncStatus();
  const [startError, setStartError] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: startSync,
    onSuccess: (status) => {
      // O 202 já traz o estado "em andamento": a consulta periódica começa daqui.
      queryClient.setQueryData(syncKey, status);
    },
    onError: (error) => {
      // Alguém (ou o salvamento da conexão) já iniciou: não é falha, só mostra o andamento.
      if (isApiError(error) && error.code === 'already_running') {
        void queryClient.invalidateQueries({ queryKey: syncKey });
        return;
      }
      setStartError(
        adminErrorMessage(error, 'Não foi possível iniciar a importação. Tente de novo.'),
      );
    },
  });

  // "Tentar de novo" some quando o andamento chega e levaria o foco ao <body>.
  const rescue = useFocusRescue(!sync.isError, headingRef);

  // Ainda não se sabe se este servidor importa: nada aparece até a primeira resposta.
  if (sync.isPending) return null;
  // 404: o servidor não tem importação. Seção escondida, sem erro.
  if (sync.data === null) return null;

  const status = sync.data;
  const running = status?.state === 'running';
  const busy = start.isPending || running;

  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 ref={headingRef} tabIndex={-1} id={headingId} className={section.cardTitle}>
        Importação de histórico
      </h3>
      <p className={forms.hint}>
        Traz para o {APP_NAME} as conversas do WhatsApp e o histórico recente delas (as últimas
        mensagens de cada conversa). Pode rodar de novo quando quiser: o que já foi importado não é
        duplicado.
      </p>

      <div className={forms.actions}>
        <Button
          variant="primary"
          busy={busy}
          onClick={() => {
            if (busy) return;
            setStartError(null);
            start.mutate();
          }}
        >
          {busy ? 'Importando…' : 'Importar histórico agora'}
        </Button>
      </div>

      {/* Região sempre presente: o leitor de tela só anuncia o que entra numa região já existente. */}
      <div role="status" className={styles.state}>
        {status && (
          <>
            <p className={styles.sentence}>{syncStateSentence(status)}</p>
            {status.state === 'error' && (
              <p className={styles.errorText}>{syncErrorHelp(status.error)}</p>
            )}
          </>
        )}
      </div>

      {status?.stats && (
        <>
          <dl className={styles.stats}>
            {syncStatRows(status.stats).map((row) => (
              <div key={row.label} className={styles.stat}>
                <dt className={styles.term}>{row.label}</dt>
                <dd className={styles.value}>{row.value}</dd>
              </div>
            ))}
          </dl>
          {status.stats.errors > 0 && (
            <p className={forms.hint}>
              Algumas conversas não puderam ser importadas; as demais foram. Importe de novo para
              tentar outra vez.
            </p>
          )}
          {status.stats.truncated && (
            <p className={forms.hint}>
              Havia mais conversas do que o limite de importação do servidor; só as mais recentes
              foram trazidas.
            </p>
          )}
        </>
      )}
      {status?.startedAt && status.state !== 'running' && (
        <p className={forms.hint}>Iniciada em {formatDateTime(status.startedAt)}.</p>
      )}

      {sync.isError && (
        <div className={styles.loadError}>
          <p role="alert" className={forms.alert}>
            Não foi possível consultar a importação.{' '}
            {adminErrorMessage(sync.error, 'Tente de novo.')}
          </p>
          <Button
            busy={sync.isFetching}
            onClick={() => {
              rescue.arm();
              void sync.refetch().then((result) => {
                if (result.isError) rescue.disarm();
              });
            }}
          >
            Tentar de novo
          </Button>
        </div>
      )}
      {startError && (
        <p role="alert" className={forms.alert}>
          {startError}
        </p>
      )}
    </section>
  );
}
